<template>
  <section class="booking-assist" aria-labelledby="booking-assist-title">
    <div class="booking-assist-header">
      <div>
        <p class="booking-assist-kicker">予約準備</p>
        <h2 id="booking-assist-title" class="booking-assist-title">
          予約する
        </h2>
      </div>
      <span v-if="pendingReservationCount > 0" class="booking-assist-count">
        未予約 {{ pendingReservationCount }}件
      </span>
    </div>

    <div class="booking-actions">
      <a
        v-for="action in primaryActions"
        :key="action.key"
        :class="['booking-action', { 'is-disabled': !action.enabled }]"
        :href="action.enabled ? action.href : undefined"
        target="_blank"
        rel="noopener noreferrer sponsored"
        :aria-disabled="!action.enabled"
        @click="handleActionClick($event, action)"
      >
        <span class="booking-action-photo-wrap">
          <img
            class="booking-action-photo"
            :src="action.image"
            :alt="action.title"
            loading="lazy"
            decoding="async"
            @error="$event.currentTarget.classList.add('is-hidden')"
          />
        </span>
        <span class="booking-action-content">
          <span class="booking-action-icon">
            <IconSvg :name="action.icon" size="sm" />
          </span>
          <span class="booking-action-text">
            <span class="booking-action-title">{{ action.title }}</span>
            <span class="booking-action-subtitle">{{ action.subtitle }}</span>
          </span>
        </span>
        <span class="booking-provider-list">
          <span
            v-for="provider in action.providers"
            :key="provider.name"
            class="booking-provider-row"
          >
            <span class="provider-name">{{ provider.name }}</span>
            <span class="provider-price">{{ provider.priceLabel }}</span>
          </span>
        </span>
      </a>
    </div>

    <div v-if="pendingReservations.length > 0" class="pending-box">
      <div class="pending-box-header">
        <span class="pending-box-title">スケジュール内の予約未完了</span>
        <span class="pending-box-subtitle">宿泊は楽天トラベルへ移動します</span>
      </div>
      <ul class="pending-list">
        <li
          v-for="item in pendingReservations"
          :key="item.LINE_NON"
          class="pending-item"
        >
          <span class="pending-item-name">{{ item.SUBJECT_NMV }}</span>
          <span class="pending-provider-summary">
            {{ getProviderSummary(item) }}
          </span>
          <a
            v-if="isHotelReservation(item)"
            class="pending-item-link"
            :href="buildReservationUrl(item)"
            target="_blank"
            rel="noopener noreferrer sponsored"
          >
            予約する
          </a>
          <span v-else class="pending-item-status">準備中</span>
        </li>
      </ul>
    </div>
    <p v-else class="booking-assist-note">
      予約が必要な予定をスケジュールに追加すると、ここに未予約リストが表示されます。
    </p>
  </section>
</template>

<script>
import { computed } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import { API_URL } from "@/config/api.js";

const fallbackDestination = "旅行先";

const actionImages = {
  hotel:
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=720&q=80",
  activity:
    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=720&q=80",
  transport:
    "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=720&q=80",
  connectivity:
    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=720&q=80",
};

function parseDestinations(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    return [];
  }
}

function toDateParam(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function appendQuery(baseUrl, params) {
  const url = new URL(baseUrl, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}

function buildRakutenAffiliateUrl(keyword, service = "hotel", context = {}) {
  return appendQuery(`${API_URL}/affiliate/rakuten-travel/redirect`, {
    keyword,
    service,
    location: context.location,
    checkinDate: context.checkinDate,
    checkoutDate: context.checkoutDate,
    adultCount: context.adultCount,
  });
}

function buildProviderRows(enabled) {
  return [
    {
      name: enabled ? "楽天トラベル" : "連携準備中",
      priceLabel: enabled ? "楽天で確認" : "準備中",
    },
  ];
}

function isHotelLike(item) {
  const category = String(item?.CATEGORY_CDV || "").toLowerCase();
  const title = String(item?.SUBJECT_NMV || "");
  return (
    category.includes("hotel") ||
    category.includes("stay") ||
    /ホテル|宿|宿泊|旅館|民宿|ペンション|リゾート|チェックイン/.test(title)
  );
}

function getRakutenService(item) {
  const category = String(item?.CATEGORY_CDV || "").toLowerCase();
  const title = String(item?.SUBJECT_NMV || "");
  if (isHotelLike(item)) return "hotel";
  if (
    category.includes("car") ||
    category.includes("rental") ||
    /レンタカー|車|ドライブ/.test(title)
  ) {
    return "rental_car";
  }
  if (
    category.includes("activity") ||
    category.includes("tour") ||
    /アクティビティ|体験|ツアー|観光|チケット|入場/.test(title)
  ) {
    return "activity";
  }
  return null;
}

export default {
  name: "BookingAssistPanel",
  components: { IconSvg },
  props: {
    shioriData: {
      type: Object,
      default: null,
    },
    schedules: {
      type: Array,
      default: () => [],
    },
    memberCount: {
      type: Number,
      default: 1,
    },
  },
  setup(props) {
    const destinations = computed(() =>
      parseDestinations(props.shioriData?.DESTINATIONS),
    );

    const destinationName = computed(
      () => destinations.value[0] || props.shioriData?.SUBJECT_NMV || "",
    );

    const searchKeyword = computed(
      () => destinationName.value || fallbackDestination,
    );

    const tripFrom = computed(() => toDateParam(props.shioriData?.TRIP_FROM));
    const tripTo = computed(() => toDateParam(props.shioriData?.TRIP_TO));
    const isInternationalTrip = computed(
      () => props.shioriData?.DESTINATION_TYPE === "international",
    );
    const adultCount = computed(() => Math.max(Number(props.memberCount || 1), 1));
    const tripContext = computed(() => ({
      location: destinationName.value,
      checkinDate: tripFrom.value,
      checkoutDate: tripTo.value,
      adultCount: adultCount.value,
    }));

    const buildReservationUrl = (item) => {
      const keyword =
        item?.SUBJECT_NMV || item?.ADDRESS_NMV || searchKeyword.value;
      const scheduleMemberCount = Array.isArray(item?.MEMBERS)
        ? item.MEMBERS.filter(Boolean).length
        : 0;
      return buildRakutenAffiliateUrl(keyword, getRakutenService(item) || "hotel", {
        location: item?.ADDRESS_NMV || destinationName.value,
        checkinDate: toDateParam(item?.START_YMD) || tripFrom.value,
        checkoutDate: toDateParam(item?.END_YMD) || tripTo.value,
        adultCount: Math.max(scheduleMemberCount || adultCount.value, 1),
      });
    };

    const rawPrimaryActions = computed(() => [
      {
        key: "hotel",
        title: "ホテル",
        subtitle: tripFrom.value
          ? `${tripFrom.value} - ${tripTo.value || "日程未定"}`
          : `${searchKeyword.value} 周辺`,
        icon: "bed-solid-full",
        image: actionImages.hotel,
        providers: buildProviderRows(true),
        href: buildRakutenAffiliateUrl(searchKeyword.value, "hotel", tripContext.value),
        enabled: true,
      },
      {
        key: "activity",
        title: "アクティビティ",
        subtitle: "楽天トラベルで体験を確認",
        icon: "camera-solid-full",
        image: actionImages.activity,
        providers: buildProviderRows(true),
        href: buildRakutenAffiliateUrl(searchKeyword.value, "activity", tripContext.value),
        enabled: true,
      },
      {
        key: "rental_car",
        title: "レンタカー",
        subtitle: "楽天トラベルでレンタカーを確認",
        icon: "car-solid",
        image: actionImages.transport,
        providers: buildProviderRows(true),
        href: buildRakutenAffiliateUrl(searchKeyword.value, "rental_car", tripContext.value),
        enabled: true,
      },
      {
        key: "connectivity",
        title: "通信",
        subtitle: "eSIM・Wi-Fi連携は準備中",
        icon: "earth-americas-solid-full",
        image: actionImages.connectivity,
        providers: buildProviderRows(false),
        href: "",
        enabled: false,
      },
    ]);

    const primaryActions = computed(() => {
      if (!isInternationalTrip.value) {
        return rawPrimaryActions.value.filter(
          (action) => action.key !== "connectivity",
        );
      }

      return rawPrimaryActions.value.map((action) => ({
        ...action,
        href: "",
        enabled: false,
        subtitle: "Coming soon",
        providers: [
          {
            name: "Coming soon",
            priceLabel: "準備中",
          },
        ],
      }));
    });

    const pendingReservations = computed(() =>
      props.schedules
        .filter(
          (item) =>
            item.RESERVATION_FLG === "1" &&
            item.RESERVATION_DONE_FLG !== "1" &&
            item.SUBJECT_NMV,
        )
        .slice(0, 4),
    );

    const pendingReservationCount = computed(
      () =>
        props.schedules.filter(
          (item) =>
            item.RESERVATION_FLG === "1" && item.RESERVATION_DONE_FLG !== "1",
        ).length,
    );

    const isRakutenReservation = (item) =>
      !isInternationalTrip.value && Boolean(getRakutenService(item));

    const getProviderSummary = (item) =>
      isRakutenReservation(item) ? "楽天トラベルで確認" : "予約連携は準備中";

    const handleActionClick = (event, action) => {
      if (!action.enabled) {
        event.preventDefault();
      }
    };

    return {
      primaryActions,
      pendingReservations,
      pendingReservationCount,
      buildReservationUrl,
      getProviderSummary,
      handleActionClick,
      isHotelReservation: isRakutenReservation,
    };
  },
};
</script>

<style scoped>
.booking-assist {
  margin-top: 1rem;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.booking-assist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-light);
}

.booking-assist-kicker {
  margin: 0 0 0.25rem;
  color: var(--primary-hover);
  font-size: 0.75rem;
  font-weight: 700;
}

.booking-assist-title {
  margin: 0;
  color: var(--color-text);
  font-size: 1.125rem;
  font-weight: 700;
}

.booking-assist-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  padding: 0 0.625rem;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: #c2410c;
  font-size: 0.8125rem;
  font-weight: 800;
  white-space: nowrap;
}

.booking-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  padding: 1rem 1.25rem 1.25rem;
}

.booking-action {
  position: relative;
  display: grid;
  align-content: end;
  min-height: 128px;
  overflow: hidden;
  color: #ffffff;
  background: linear-gradient(135deg, #7d8768 0%, #b4cf9e 100%);
  border-radius: var(--radius-md);
  text-decoration: none;
  box-shadow: var(--shadow-sm);
  transition:
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

.booking-action:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.booking-action.is-disabled {
  cursor: default;
  filter: grayscale(0.15);
}

.booking-action.is-disabled:hover {
  box-shadow: var(--shadow-sm);
  transform: none;
}

.booking-action-photo-wrap {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #7d8768 0%, #b4cf9e 100%);
}

.booking-action-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.booking-action-photo.is-hidden {
  display: none;
}

.booking-action-photo-wrap::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(
    180deg,
    rgba(17, 24, 39, 0.08) 0%,
    rgba(17, 24, 39, 0.68) 100%
  );
}

.booking-action.is-disabled .booking-action-photo-wrap::after {
  background: linear-gradient(
    180deg,
    rgba(17, 24, 39, 0.28) 0%,
    rgba(17, 24, 39, 0.78) 100%
  );
}

.booking-action-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  gap: 0.625rem;
  width: 100%;
  padding: 0.875rem;
}

.booking-provider-list {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.375rem;
  margin: 0 0.875rem 0.875rem;
  padding: 0.625rem;
  background: rgba(255, 255, 255, 0.94);
  border-radius: var(--radius-md);
}

.booking-provider-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
}

.provider-name {
  min-width: 0;
  overflow: hidden;
  color: var(--color-text);
  font-size: 0.75rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.provider-price {
  flex-shrink: 0;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 800;
}

.booking-action.is-disabled .provider-price {
  color: #6b7280;
}

.booking-action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  color: var(--primary-hover);
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--radius-md);
}

.booking-action-text {
  display: grid;
  gap: 0.125rem;
  min-width: 0;
}

.booking-action-title {
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.2;
}

.booking-action-subtitle {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8125rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pending-box {
  margin: 0 1.25rem 1.25rem;
  padding: 0.875rem;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: var(--radius-md);
}

.pending-box-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
}

.pending-box-title {
  color: #9a3412;
  font-size: 0.875rem;
  font-weight: 700;
}

.pending-box-subtitle {
  color: #c2410c;
  font-size: 0.75rem;
}

.pending-list {
  display: grid;
  gap: 0.5rem;
  padding: 0;
  margin: 0;
  list-style: none;
}

.pending-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.25rem 0.75rem;
}

.pending-item-name {
  min-width: 0;
  overflow: hidden;
  color: var(--color-text);
  font-size: 0.8125rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pending-item-link,
.pending-item-status {
  grid-row: 1 / 3;
  grid-column: 2;
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 700;
}

.pending-item-link {
  color: #2563eb;
  text-decoration: none;
}

.pending-item-status {
  color: #6b7280;
}

.pending-provider-summary {
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pending-item-link:hover {
  text-decoration: underline;
}

.booking-assist-note {
  margin: 0;
  padding: 0 1.25rem 1.25rem;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.6;
}

@media (min-width: 640px) {
  .booking-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
